/**
 * Step3_RiskScoring Component
 *
 * Third step of the wizard - Score each hazard using the 5×5 risk matrix.
 *
 * @module components/Step3_RiskScoring
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  Users,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { RiskMatrix, RiskLevelBadge } from './RiskMatrix';
import {
  getRiskLevel,
  getAlarpZone,
  LIKELIHOOD_SCALE,
  SEVERITY_SCALE,
  ALARP_ZONES
} from '../config/moduleConfig';

/**
 * Get background color for risk level
 */
const getRiskBgColor = (score) => {
  if (score >= 20) return 'bg-red-100';
  if (score >= 12) return 'bg-orange-100';
  if (score >= 6) return 'bg-yellow-100';
  return 'bg-green-100';
};

/**
 * Get ALARP zone display info
 */
const getAlarpInfo = (score) => {
  const zone = getAlarpZone(score);
  return ALARP_ZONES[zone];
};

/**
 * Step3_RiskScoring Component
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.setFormData - Function to update form data
 */
export const Step3_RiskScoring = ({ formData, setFormData }) => {
  const [expandedHazard, setExpandedHazard] = useState(0);

  const hazards = formData.hazards || [];

  const updateHazard = (index, updates) => {
    setFormData(prev => ({
      ...prev,
      hazards: prev.hazards.map((hazard, i) =>
        i === index ? { ...hazard, ...updates } : hazard
      )
    }));
  };

  const toggleHazard = (index) => {
    setExpandedHazard(expandedHazard === index ? -1 : index);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Risk Scoring</h3>
        <p className="text-sm text-gray-600 mt-1">
          Score each hazard's likelihood and severity before controls are applied (Initial Risk)
        </p>
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-4 gap-3">
        {['extreme', 'high', 'medium', 'low'].map(level => {
          const count = hazards.filter(h => {
            const score = (h.initialLikelihood || 1) * (h.initialSeverity || 1);
            return getRiskLevel(score) === level;
          }).length;

          const colorClasses = {
            extreme: 'bg-red-100 text-red-800 border-red-200',
            high: 'bg-orange-100 text-orange-800 border-orange-200',
            medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            low: 'bg-green-100 text-green-800 border-green-200'
          };

          return (
            <div
              key={level}
              className={`p-3 rounded-lg border ${colorClasses[level]}`}
            >
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-sm font-medium capitalize">{level}</p>
            </div>
          );
        })}
      </div>

      {/* Hazard Scoring Cards */}
      {hazards.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
          <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No hazards to score</p>
          <p className="text-sm text-gray-500 mt-1">
            Go back to Step 2 to add hazards
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {hazards.map((hazard, index) => {
            const score = (hazard.initialLikelihood || 1) * (hazard.initialSeverity || 1);
            const isExpanded = expandedHazard === index;
            const alarpInfo = getAlarpInfo(score);

            return (
              <div
                key={hazard.id}
                className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden"
              >
                {/* Hazard Header */}
                <button
                  onClick={() => toggleHazard(index)}
                  className="w-full bg-slate-100 px-5 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`h-5 w-5 ${
                      score >= 20 ? 'text-red-500' :
                      score >= 12 ? 'text-orange-500' :
                      score >= 6 ? 'text-yellow-500' :
                      'text-green-500'
                    }`} />
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Hazard {index + 1}: {hazard.hazardDescription?.substring(0, 50)}
                        {hazard.hazardDescription?.length > 50 ? '...' : ''}
                      </h4>
                      {hazard.whoAffected && (
                        <p className="text-sm text-gray-600 mt-0.5">
                          <Users className="h-3 w-3 inline mr-1" />
                          {hazard.whoAffected}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <RiskLevelBadge score={score} />
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Risk Matrix */}
                      <div>
                        <h5 className="font-medium text-gray-900 mb-4">Select Initial Risk Level</h5>
                        <RiskMatrix
                          selectedLikelihood={hazard.initialLikelihood || 1}
                          selectedSeverity={hazard.initialSeverity || 1}
                          onSelect={(likelihood, severity) => {
                            updateHazard(index, {
                              initialLikelihood: likelihood,
                              initialSeverity: severity
                            });
                          }}
                        />
                      </div>

                      {/* Risk Score Details */}
                      <div className="space-y-4">
                        <h5 className="font-medium text-gray-900">Risk Score Details</h5>

                        {/* Likelihood */}
                        <div>
                          <label className="text-sm text-gray-600">Likelihood</label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-gray-900">
                                {LIKELIHOOD_SCALE[hazard.initialLikelihood || 1]?.label}
                              </p>
                              <span className="text-lg font-bold text-gray-700">
                                {hazard.initialLikelihood || 1}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {LIKELIHOOD_SCALE[hazard.initialLikelihood || 1]?.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {LIKELIHOOD_SCALE[hazard.initialLikelihood || 1]?.frequency}
                            </p>
                          </div>
                        </div>

                        {/* Severity */}
                        <div>
                          <label className="text-sm text-gray-600">Severity</label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-gray-900">
                                {SEVERITY_SCALE[hazard.initialSeverity || 1]?.label}
                              </p>
                              <span className="text-lg font-bold text-gray-700">
                                {hazard.initialSeverity || 1}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {SEVERITY_SCALE[hazard.initialSeverity || 1]?.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {SEVERITY_SCALE[hazard.initialSeverity || 1]?.medicalTreatment}
                            </p>
                          </div>
                        </div>

                        {/* Risk Score */}
                        <div>
                          <label className="text-sm text-gray-600">Initial Risk Score</label>
                          <div className={`mt-1 p-4 rounded-lg ${getRiskBgColor(score)}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-3xl font-bold text-gray-900">
                                  {score}
                                </p>
                                <p className="text-sm text-gray-600">out of 25</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-semibold capitalize">
                                  {getRiskLevel(score)}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {alarpInfo?.label}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ALARP Action Required */}
                        <div className={`p-3 rounded-lg border ${
                          score >= 20 ? 'bg-red-50 border-red-200' :
                          score >= 6 ? 'bg-yellow-50 border-yellow-200' :
                          'bg-green-50 border-green-200'
                        }`}>
                          <p className={`text-sm font-medium ${
                            score >= 20 ? 'text-red-900' :
                            score >= 6 ? 'text-yellow-900' :
                            'text-green-900'
                          }`}>
                            {alarpInfo?.action}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Scoring Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Scoring Tips</h4>
            <ul className="text-sm text-blue-800 mt-2 space-y-1">
              <li>
                <strong>Likelihood:</strong> Consider how often this hazard could realistically occur
              </li>
              <li>
                <strong>Severity:</strong> Consider the worst-case outcome if the hazard materializes
              </li>
              <li>
                <strong>Be realistic:</strong> Don't over or under-estimate - use your experience and evidence
              </li>
              <li>
                <strong>Initial risk:</strong> Score as if no controls were in place
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quick Reference Tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Likelihood Reference */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <h5 className="font-medium text-gray-900 text-sm">Likelihood Scale</h5>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(LIKELIHOOD_SCALE).map(([value, data]) => (
              <div key={value} className="px-4 py-2 flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">{value}</span>
                  <span className="text-gray-600 ml-2">- {data.label}</span>
                </div>
                <span className="text-xs text-gray-500">{data.description}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Severity Reference */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <h5 className="font-medium text-gray-900 text-sm">Severity Scale</h5>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(SEVERITY_SCALE).map(([value, data]) => (
              <div key={value} className="px-4 py-2 flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">{value}</span>
                  <span className="text-gray-600 ml-2">- {data.label}</span>
                </div>
                <span className="text-xs text-gray-500">{data.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3_RiskScoring;
